import axios, { AxiosResponse, AxiosError } from 'axios';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface ApiCallParams<T> {
    url: string;
    method: HttpMethod;
    payload?: T;
    headers?: Record<string, string>;
}

interface ApiResponse<R> {
    statusCode: number;
    body: string; // The body is a stringified JSON
    error?: string;
}

interface ApiCallReturn<R> {
    statusCode: number;
    message: string;
    data: R | null;
    isSuccess: boolean;
}

async function apiCall<T, R>({ url, method, payload, headers }: ApiCallParams<T>): Promise<ApiCallReturn<R>> {
    try {
        console.log(`Making API call to ${url} with payload:`, JSON.stringify(payload, null, 2));

        const response: AxiosResponse<ApiResponse<R>> = await axios({
            url,
            method,
            data: payload,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
        });

        // Parse the stringified JSON in the body
        const parsedBody = JSON.parse(response.data.body);

        if (response.status !== 200 || response.data.statusCode !== 200) {
            return {
                statusCode: response.data.statusCode,
                message: parsedBody.message || 'An unexpected error occurred. Please try again later.',
                data: null,
                isSuccess: false,
            };
        }

        return {
            statusCode: response.data.statusCode,
            message: parsedBody.message || 'Operation successful.',
            data: parsedBody.data || null,
            isSuccess: true,
        };
    } catch (error: unknown) {
        let userFriendlyMessage = 'Failed to retrieve data. Please try again later.';
        let statusCode = 500;

        if (axios.isAxiosError(error)) {
            statusCode = error.response?.status || 500;

            if (statusCode === 400) {
                userFriendlyMessage = 'There was a problem with your request. Please check the details and try again.';
            } else if (statusCode === 500) {
                userFriendlyMessage = 'The server encountered an error. Please try again later.';
            }
        }

        console.error(`Error during API call to ${url}:`, error);
        console.error(`Payload: ${JSON.stringify(payload, null, 2)}`);

        return {
            statusCode,
            message: userFriendlyMessage,
            data: null,
            isSuccess: false,
        };
    }
}

export { apiCall };